/**
 * Design Tokens - Single Source of Truth for Design Decisions
 *
 * This file centralizes all design decisions to ensure consistency across the site.
 * Use these constants instead of magic strings in components.
 *
 * @see /docs/design/ux-ui-consistency-analysis.md for rationale and usage guidelines
 */

// ============================================================================
// 📑 TABLE OF CONTENTS
// ============================================================================
/**
 * Quick Navigation (search for the constant name)
 *
 * LAYOUT & CONTAINERS
 *   - CONTAINER_WIDTHS - Semantic container width patterns
 *   - CONTAINER_PADDING - Horizontal padding
 *   - CONTAINER_VERTICAL_PADDING - Vertical padding
 *   - MOBILE_SAFE_PADDING - Mobile bottom padding
 *   - getContentDepthClass() - Content depth classifier
 *   - ACTIVITY_IMAGE - Activity feed images
 *
 * TYPOGRAPHY
 *   - TYPOGRAPHY - Fluid typography variants
 *   - CONTENT_HIERARCHY - Content block patterns
 *   - PROGRESSIVE_TEXT - Progressive text depth
 *   - FONT_CONTRAST - Font contrast system
 *
 * SPACING
 *   - SPACING - Vertical spacing patterns
 *   - SPACING_VALUES - Numeric spacing [DEPRECATED]
 *   - SPACING_SCALE - Type-safe spacing scale
 *   - spacing() - Spacing helper function
 *
 * COLORS
 *   - SEMANTIC_COLORS - Semantic color tokens
 *   - SERIES_COLORS - Series/theme colors
 *
 * EFFECTS & VISUAL
 *   - HOVER_EFFECTS - Interactive hover states
 *   - ANIMATION - CSS animation classes
 *   - ANIMATION_CONSTANTS - Animation timing/easing
 *   - BORDERS - Border styles
 *   - SHADOWS - Shadow variations
 *
 * INTERACTION
 *   - TOUCH_TARGET - Touch target sizes
 *   - Z_INDEX - Z-index stacking layers
 *   - FOCUS_RING - Focus ring styles
 *   - SCROLL_OFFSET - Scroll offset values
 *
 * LAYOUT PATTERNS
 *   - PAGE_LAYOUT - Page layout patterns
 *   - HERO_VARIANTS - Hero section variants
 *   - SCROLL_BEHAVIOR - Scroll animations
 *   - GRID_PATTERNS - Grid layout patterns
 *
 * COMPONENTS & APP
 *   - IMAGE_PLACEHOLDER - Image placeholder config
 *   - APP_TOKENS - App-specific tokens
 *   - ARCHIVE_CARD_VARIANTS - Archive card styles
 *   - ARCHIVE_ANIMATIONS - Framer Motion variants
 *   - VIEW_MODES - View mode configurations
 *
 * TYPE EXPORTS
 *   - TypeScript type definitions for token keys
 */

// ============================================================================
// FLUID TYPOGRAPHY UTILITIES
// ============================================================================

/**
 * Fluid typography using CSS clamp() for viewport-responsive sizing
 *
 * Benefits:
 * - Eliminates 90% of media query-based font sizing
 * - Continuous scaling across all viewports
 * - Respects user zoom preferences (accessibility)
 * - Better performance (no breakpoint recalculations)
 *
 * Formula: clamp(min-rem, calc(base-rem + viewport-multiplier), max-rem)
 *
 * @see Modern UI/UX Design Standards Report (2025)
 * @see https://web.dev/articles/baseline-in-action-fluid-type
 */

/**
 * Tailwind size reference (for conversion):
 * - text-sm: 0.875rem (14px)
 * - text-base: 1rem (16px)
 * - text-lg: 1.125rem (18px)
 * - text-xl: 1.25rem (20px)
 * - text-2xl: 1.5rem (24px)
 * - text-3xl: 1.875rem (30px)
 * - text-4xl: 2.25rem (36px)
 * - text-5xl: 3rem (48px)
 */

/**
 * Converts fixed Tailwind sizes to fluid clamp() values
 *
 * Examples:
 * - text-3xl md:text-4xl → text-[clamp(1.875rem,4vw+1rem,2.25rem)]
 * - text-xl md:text-2xl → text-[clamp(1.25rem,2.5vw+0.75rem,1.5rem)]
 * - text-lg md:text-xl → text-[clamp(1.125rem,2vw+0.75rem,1.25rem)]
 */

// ============================================================================
// CONTAINER WIDTHS
// ============================================================================

/**
 * Semantic container width patterns based on content type
 *
 * @example
 * ```tsx
 * <div className={`mx-auto ${CONTAINER_WIDTHS.standard} ${CONTAINER_PADDING}`}>
 *   {content}
 * </div>
 * ```
 */
export const CONTAINER_WIDTHS = {
  /** Prose/reading content - optimal line length (45-75 chars/line per typography research) */
  prose: 'max-w-4xl',

  /** Narrow width for forms and focused content (contact forms) */
  narrow: 'max-w-4xl',

  /** Thread-style single-column feed (Threads-inspired activity timeline) */
  thread: 'max-w-2xl',

  /** Standard width for core pages (homepage, about, contact, resume) */
  standard: 'max-w-5xl',

  /** Content-heavy pages with sidebar (individual blog posts, project detail pages) */
  content: 'max-w-6xl',

  /** Archive/listing pages with filters and grids (blog listing, projects listing) */
  archive: 'max-w-7xl',

  /** Full-width dashboard pages with data tables, charts, and analytics (dashboard pages, dev tools) */
  dashboard: 'max-w-[1536px]',
} as const;

/**
 * Standard horizontal padding for all containers
 * Provides consistent edge spacing across breakpoints
 */
export const CONTAINER_PADDING = 'px-4 sm:px-6 md:px-8' as const;

/**
 * Standard vertical padding for page containers
 * Provides consistent top/bottom spacing
 */
export const CONTAINER_VERTICAL_PADDING = 'py-8 md:py-12' as const;

/**
 * Mobile-safe bottom padding that accounts for BottomNav (64px) + safe clearance
 * - Mobile: 96px (BottomNav 64px + 32px clearance)
 * - Desktop: 32px (standard padding, no BottomNav)
 */
export const MOBILE_SAFE_PADDING = 'pb-24 md:pb-8' as const;

/**
 * Dynamic Content Depth Classifier
 *
 * Analyzes content characteristics and returns appropriate depth styling.
 * Implements varying depth based on paragraph length and position.
 *
 * @param options - Content analysis options
 * @returns Appropriate depth class string
 *
 * @example
 * ```tsx
 * const depthClass = getContentDepthClass({
 *   length: paragraph.length,
 *   position: 'body',
 *   isContextual: false
 * });
 * <p className={depthClass}>{paragraph}</p>
 * ```
 */
export function getContentDepthClass(options: {
  /** Character length of content */
  length?: number;
  /** Position in content flow */
  position?: 'opening' | 'body' | 'closing';
  /** Whether content is contextual/supporting */
  isContextual?: boolean;
  /** Whether to apply font contrast system */
  useFontContrast?: boolean;
}): string {
  const { length = 0, position = 'body', isContextual = false, useFontContrast = false } = options;

  // Base font system
  if (useFontContrast) {
    return FONT_CONTRAST.base;
  }

  // Contextual content
  if (isContextual) {
    return PROGRESSIVE_TEXT.contextual;
  }

  // Position-based styling
  if (position === 'opening') {
    return PROGRESSIVE_TEXT.opening;
  }

  if (position === 'closing') {
    return PROGRESSIVE_TEXT.closing;
  }

  // Length-based styling
  if (length > 300) {
    return PROGRESSIVE_TEXT.extended;
  }

  return PROGRESSIVE_TEXT.body;
}

// ============================================================================
// ACTIVITY IMAGE PRESENTATION
// ============================================================================

/**
 * Activity feed image presentation tokens (Medium/Substack inspired)
 * Provides consistent aspect ratios and sizing for featured images in activities
 *
 * @example
 * ```tsx
 * {activity.meta?.image && (
 *   <div className={cn(ACTIVITY_IMAGE.container, ACTIVITY_IMAGE.sizes.header)}>
 *     <Image src={activity.meta.image.url} fill className={ACTIVITY_IMAGE.image} />
 *   </div>
 * )}
 * ```
 */
export const ACTIVITY_IMAGE = {
  /** Base container with 16:9 aspect ratio and overflow handling, with light background for transparent images */
  container: 'aspect-[16/9] relative overflow-hidden rounded-lg mb-4 bg-muted/30 dark:bg-muted/10',

  /** Image size variants for different activity types - width controls size via aspect ratio */
  sizes: {
    /** Primary activity header (larger, more prominent) - full width, aspect ratio controls height */
    header: 'w-full',
    /** Reply/nested activity (smaller, compact) - full width, aspect ratio controls height */
    reply: 'w-full',
  },

  /** Image styling with hover zoom effect */
  image: 'object-cover w-full h-full transition-transform duration-300 hover:scale-105',
} as const;

// ============================================================================
// TYPOGRAPHY
// ============================================================================

/**
 * Typography patterns for consistent heading hierarchy and text styles
 *
 * @example
 * ```tsx
 * <h1 className={TYPOGRAPHY.h1.standard}>Page Title</h1>
 * <p className={TYPOGRAPHY.description}>Page description</p>
 * ```
 */
export const TYPOGRAPHY = {
  /** H1 heading variants for different contexts */
  h1: {
    /** Standard page titles (about, projects, contact, blog listing)
     * Fluid: 30px (mobile) → 36px (desktop)
     */
    standard: 'text-[clamp(1.875rem,4vw+1rem,2.25rem)] font-semibold tracking-tight',

    /** Archive/listing page titles (about, blog archive, portfolio archive)
     * Fluid: 30px (mobile) → 36px (desktop)
     */
    hero: 'font-heading text-[clamp(1.875rem,4vw+1rem,2.25rem)] font-semibold tracking-tight ws-sm',

    /** Blog post titles (larger, more prominent)
     * Fluid: 30px (mobile) → 48px (desktop)
     */
    article:
      'font-heading text-[clamp(1.875rem,5vw+0.75rem,3rem)] font-semibold tracking-tight leading-tight ws-md',

    /** Portfolio project titles
     * Fluid: 30px (mobile) → 48px (desktop)
     */
    project:
      'font-heading text-[clamp(1.875rem,5vw+0.75rem,3rem)] font-semibold tracking-tight leading-tight ws-md',

    /** MDX content headings
     * Fluid: 30px (mobile) → 36px (desktop)
     */
    mdx: 'text-[clamp(1.875rem,4vw+1rem,2.25rem)] font-semibold tracking-tight ws-sm',
  },

  /** H2 heading variants */
  h2: {
    /** Standard section headings
     * Fluid: 20px (mobile) → 24px (desktop)
     */
    standard: 'text-[clamp(1.25rem,2.5vw+0.75rem,1.5rem)] font-medium ws-xs',

    /** Featured content headings (blog post cards, featured sections)
     * Fluid: 24px (mobile) → 30px (desktop)
     */
    featured:
      'font-heading text-[clamp(1.5rem,3vw+0.875rem,1.875rem)] font-semibold tracking-tight ws-sm',

    /** MDX content headings
     * Fluid: 24px (mobile) → 30px (desktop)
     */
    mdx: 'text-[clamp(1.5rem,3vw+0.875rem,1.875rem)] font-semibold tracking-tight ws-sm',
  },

  /** H3 heading variants */
  h3: {
    /** Standard subsection headings
     * Fluid: 18px (mobile) → 20px (desktop)
     */
    standard: 'text-[clamp(1.125rem,2vw+0.75rem,1.25rem)] font-medium ws-xs',

    /** MDX content headings
     * Fluid: 18px (mobile) → 20px (desktop)
     */
    mdx: 'font-heading text-[clamp(1.125rem,2vw+0.75rem,1.25rem)] font-bold tracking-tight ws-xs',
  },

  /** H4 heading variants */
  h4: {
    /** MDX content headings
     * Fluid: 16px (mobile) → 18px (desktop)
     */
    mdx: 'font-heading text-[clamp(1rem,1.5vw+0.75rem,1.125rem)] font-bold tracking-tight',
  },

  /** H5 heading variants */
  h5: {
    /** MDX content headings
     * Fluid: 14px (mobile) → 16px (desktop)
     */
    mdx: 'font-heading text-[clamp(0.875rem,1vw+0.75rem,1rem)] font-semibold tracking-tight',
  },

  /** H6 heading variants */
  h6: {
    /** MDX content headings
     * Stays at 14px across viewports
     */
    mdx: 'font-heading text-sm font-semibold tracking-tight',
  },

  /** Special display text (stats, error titles, large numbers) */
  display: {
    /** Error page titles
     * Fluid: 30px (mobile) → 36px (desktop)
     */
    error: 'text-[clamp(1.875rem,4vw+1rem,2.25rem)] font-bold ws-xs',

    /** Large statistics/metrics display
     * Stays at 30px across viewports (single value, no scaling needed)
     */
    stat: 'text-3xl font-bold tracking-tight ws-xs',

    /** Extra large statistics display (homepage stats)
     * Fluid: 36px (mobile) → 48px (desktop)
     */
    statLarge: 'text-[clamp(2.25rem,4.5vw+1rem,3rem)] font-bold ws-sm',
  },

  /** Lead text / page descriptions
   * Fluid: 18px (mobile) → 20px (desktop)
   */
  description: 'text-[clamp(1.125rem,2vw+0.75rem,1.25rem)] text-muted-foreground ws-xs',

  /** Metadata text (dates, reading time, etc.) */
  metadata: 'text-sm text-muted-foreground',

  /** Body text for long-form content */
  body: 'text-base text-foreground leading-relaxed ws-xs',

  /** Label and small UI text */
  label: {
    /** Card/section labels (e.g., "Trending Posts", "Recommendations") */
    standard: 'text-base font-semibold',
    /** Smaller labels for list items, badges */
    small: 'text-sm font-semibold',
    /** Extra small labels */
    xs: 'text-xs font-semibold',
  },

  /** Activity feed typography (content-focused, Medium/Substack inspired) */
  activity: {
    /** Primary activity titles (larger, prominent, good hierarchy)
     * Fluid: 20px (mobile) → 24px (tablet) → 30px (desktop)
     */
    title: 'text-[clamp(1.25rem,3vw+0.75rem,1.875rem)] font-semibold tracking-tight',

    /** Activity subtitles (optional secondary text)
     * Fluid: 16px (mobile) → 18px (desktop)
     */
    subtitle: 'text-[clamp(1rem,1.5vw+0.75rem,1.125rem)] text-muted-foreground',

    /** Activity descriptions (enhanced readability, optimal line height)
     * Stays at 16px (base size, no scaling needed)
     */
    description: 'text-base leading-relaxed text-foreground/90',

    /** Activity metadata (timestamps, counts, badges)
     * Stays at 14px (small text, no scaling needed)
     */
    metadata: 'text-sm text-muted-foreground',

    /** Reply/nested activity titles (smaller than primary, still readable)
     * Fluid: 16px (mobile) → 18px (desktop)
     */
    replyTitle: 'text-[clamp(1rem,1.5vw+0.75rem,1.125rem)] font-medium',

    /** Reply descriptions (compact but readable)
     * Stays at 14px (small text, no scaling needed)
     */
    replyDescription: 'text-sm leading-relaxed text-muted-foreground',
  },

  /** Accordion/FAQ specific styling */
  accordion: {
    /** FAQ section heading */
    heading: 'font-semibold text-2xl',
    /** FAQ question trigger */
    trigger: 'text-left text-lg font-bold',
  },

  /** Logo/branding text */
  logo: {
    /** Small logo text (mobile nav, compact views)
     * Stays at 14px (small text, no scaling needed)
     */
    small: 'text-sm font-heading font-semibold leading-none',

    /** Medium logo text (default)
     * Fluid: 20px (mobile) → 24px (desktop)
     */
    medium: 'text-[clamp(1.25rem,2.5vw+0.75rem,1.5rem)] font-heading font-semibold leading-none',

    /** Large logo text (headers, hero sections)
     * Fluid: 30px (mobile) → 36px (desktop)
     */
    large: 'text-[clamp(1.875rem,4vw+1rem,2.25rem)] font-heading font-semibold leading-none',

    /** Extra large logo text (main homepage header)
     * Fluid: 36px (mobile) → 48px (desktop)
     */
    xlarge: 'text-[clamp(2.25rem,4.5vw+1rem,3rem)] font-heading font-semibold leading-none',
  },

  /**
   * Depth-Based Text Hierarchy
   *
   * Creates visual hierarchy through progressive text treatments.
   * Inspired by the about page's varying depth patterns.
   *
   * Usage: Apply to content blocks, subsections, and contextual information
   *
   * @example
   * ```tsx
   * <div className={TYPOGRAPHY.depth.primary}>Main content here</div>
   * <div className={TYPOGRAPHY.depth.secondary}>Supporting information</div>
   * <div className={TYPOGRAPHY.depth.tertiary}>Contextual details</div>
   * ```
   */
  depth: {
    /** Primary content - emphasized, full contrast */
    primary: 'font-medium text-foreground',

    /** Secondary content - medium emphasis, slight reduction */
    secondary: 'font-normal text-foreground/90',

    /** Tertiary content - supporting information, muted */
    tertiary: 'font-normal text-muted-foreground',

    /** Accent content - highlighted information */
    accent: 'font-semibold text-foreground',

    /** Subtle content - least emphasis, background information */
    subtle: 'font-light text-muted-foreground/70',
  },
} as const;

// ============================================================================
// CONTENT HIERARCHY
// ============================================================================

/**
 * Content Hierarchy Patterns
 *
 * Defines consistent patterns for creating depth and emphasis in content blocks.
 * Based on the varying depth style from the about page, these patterns create
 * visual hierarchy through strategic use of font weights and text opacity.
 *
 * Use Cases:
 * - Blog post content sections
 * - About page information blocks
 * - Card descriptions and supporting text
 * - Contextual information panels
 *
 * @example
 * ```tsx
 * <div className={CONTENT_HIERARCHY.primary.container}>
 *   <h3 className={CONTENT_HIERARCHY.primary.title}>Section Title</h3>
 *   <p className={CONTENT_HIERARCHY.primary.content}>Main content here</p>
 *   <p className={CONTENT_HIERARCHY.supporting.content}>Supporting details</p>
 * </div>
 * ```
 */
export const CONTENT_HIERARCHY = {
  /** Primary content blocks - main information */
  primary: {
    title: 'font-medium text-foreground',
    content: 'text-foreground leading-relaxed',
    container: 'space-y-3',
  },

  /** Supporting content blocks - contextual information */
  supporting: {
    title: 'font-medium text-foreground/90',
    content: 'text-muted-foreground leading-relaxed',
    container: 'space-y-2 mt-1',
  },

  /** Accent content blocks - highlighted information */
  accent: {
    title: 'font-semibold text-foreground',
    content: 'text-foreground/95 leading-relaxed',
    container: 'space-y-3',
  },

  /** Subtle content blocks - background information */
  subtle: {
    title: 'font-normal text-muted-foreground',
    content: 'text-muted-foreground/80 leading-relaxed text-sm',
    container: 'space-y-2',
  },
} as const;

/**
 * Progressive Text Patterns
 *
 * Dynamic text styling based on paragraph position or content length.
 * Implements the "muted text dynamically by paragraph length" concept.
 *
 * Usage: Apply programmatically based on content analysis or position.
 *
 * @example
 * ```tsx
 * // For first paragraph
 * <p className={PROGRESSIVE_TEXT.opening}>Introduction text</p>
 *
 * // For subsequent paragraphs
 * <p className={PROGRESSIVE_TEXT.body}>Regular body text</p>
 *
 * // For final/closing paragraphs
 * <p className={PROGRESSIVE_TEXT.closing}>Closing thoughts</p>
 * ```
 */
export const PROGRESSIVE_TEXT = {
  /** Opening paragraph - full emphasis */
  opening: 'text-foreground font-normal leading-relaxed text-lg',

  /** Body paragraphs - standard treatment */
  body: 'text-foreground leading-relaxed',

  /** Long paragraphs - slightly reduced emphasis */
  extended: 'text-foreground/95 leading-relaxed text-[15px]',

  /** Closing paragraphs - subtle reduction */
  closing: 'text-foreground/90 leading-relaxed',

  /** Contextual paragraphs - background information */
  contextual: 'text-muted-foreground leading-relaxed text-sm',
} as const;

/**
 * Font Weight Contrast System
 *
 * Establishes clear contrast between thin base fonts and bold weights.
 * Addresses the "thinner base font in contrast to bold weight" requirement.
 *
 * @example
 * ```tsx
 * <p className={FONT_CONTRAST.base}>Base content with thin weight</p>
 * <strong className={FONT_CONTRAST.emphasis}>Bold contrast text</strong>
 * <h3 className={FONT_CONTRAST.heading}>Heading with proper contrast</h3>
 * ```
 */
export const FONT_CONTRAST = {
  /** Base text - lighter weight for better contrast */
  base: 'font-light text-foreground leading-relaxed',

  /** Medium emphasis - slight increase from base */
  medium: 'font-normal text-foreground leading-relaxed',

  /** Strong emphasis - clear contrast from base */
  emphasis: 'font-semibold text-foreground',

  /** Bold elements - maximum contrast */
  bold: 'font-bold text-foreground',

  /** Heading contrast - optimized for headings */
  heading: 'font-medium text-foreground',
} as const;

// ============================================================================
// SPACING
// ============================================================================

/**
 * Spacing patterns for consistent vertical rhythm
 *
 * IMPORTANT: These tokens are for VERTICAL spacing (space-y-*) only.
 * For inline/horizontal spacing (gap-*, p-*, px-*, py-*), use numeric values.
 *
 * Valid Usage:
 * ✅ <div className={SPACING.section}>        // Vertical spacing between sections
 * ✅ <div className="flex gap-4">            // Horizontal gap (use numbers)
 * ✅ <div className="space-y-2">             // Small vertical spacing (use numbers)
 *
 * Invalid Usage:
 * ❌ <div className={`gap-${SPACING.compact}`}>     // SPACING has no 'compact' property
 * ❌ <div className={`space-y-${SPACING.tight}`}>  // SPACING has no 'tight' property
 * ❌ <div className={`p-${SPACING.content}`}>      // SPACING is for space-y only
 *
 * Available Properties:
 * - section: Major page sections (space-y-8 md:space-y-10 lg:space-y-14)
 * - subsection: Related content blocks (space-y-5 md:space-y-6 lg:space-y-8)
 * - content: Within content blocks (space-y-3 md:space-y-4 lg:space-y-5)
 * - prose: Running text paragraphs (space-y-5 md:space-y-6 lg:space-y-8)
 * - compact: Lists and alerts (space-y-2)
 *
 * @example Major page sections (largest gaps)
 * ```tsx
 * <div className={SPACING.section}>
 *   <section>Hero Section</section>
 *   <section>Features Section</section>
 *   <section>CTA Section</section>
 * </div>
 * ```
 *
 * @example Related content blocks within a section
 * ```tsx
 * <section>
 *   <h2>About Me</h2>
 *   <div className={SPACING.subsection}>
 *     <div>Experience block</div>
 *     <div>Skills block</div>
 *     <div>Education block</div>
 *   </div>
 * </section>
 * ```
 *
 * @example Content within a block (tightest spacing)
 * ```tsx
 * <div className={SPACING.content}>
 *   <h3>Project Title</h3>
 *   <p>Project description</p>
 *   <ul>Tech stack</ul>
 * </div>
 * ```
 *
 * @example Correct usage with numbers for gaps and padding
 * ```tsx
 * // ✅ Use numbers for flex/grid gaps
 * <div className="flex gap-4">
 *   <Button>Primary</Button>
 *   <Button>Secondary</Button>
 * </div>
 *
 * // ✅ Use numbers for padding
 * <div className="p-6 rounded-lg">
 *   <p>Card content</p>
 * </div>
 *
 * // ✅ Use numbers for tight vertical spacing
 * <ul className="space-y-2">
 *   <li>Item 1</li>
 *   <li>Item 2</li>
 * </ul>
 * ```
 */
export const SPACING = {
  /** Between major page sections (largest gaps) */
  section: 'space-y-8 md:space-y-10 lg:space-y-14',

  /** Between related content blocks within a section */
  subsection: 'space-y-5 md:space-y-6 lg:space-y-8',

  /** Within content blocks (tightest spacing) */
  content: 'space-y-3 md:space-y-4 lg:space-y-5',
  // NOTE: Do NOT use SPACING.content in ArticleLayout wrapper
  // Blog content uses prose CSS classes from globals.css for natural paragraph spacing

  /** Running text and prose paragraphs (better readability for long-form content) */
  prose: 'space-y-5 md:space-y-6 lg:space-y-8',
  // NOTE: Only use SPACING.prose for manually structured sections
  // Blog/article content relies on prose CSS classes, not design tokens

  /** Page hero/header sections with prose wrapper */
  proseHero: 'prose space-y-4',

  /** Generic prose sections */
  proseSection: 'prose space-y-4',

  /** Compact vertical spacing for lists and alerts (improved mobile space utilization) */
  compact: 'space-y-2',

  /** Reduced spacing for list items (optimized for mobile scaling) - minimal gap between items */
  list: '',

  /** Blog post card lists (compact, optimized spacing for post grids) */
  postList: 'space-y-2',

  /** Image elements in blog content (top/bottom margins) */
  image: 'my-6 md:my-8',

  /** Blog post layout spacing (intentional gap-8 for visual hierarchy) */
  blogLayout: 'gap-8',

  /** Content grid spacing (card grids, portfolio items) */
  contentGrid: 'gap-6',

  /** Alternative subsection spacing (backwards compatibility, single value) */
  subsectionAlt: 'space-y-6',

  /** Section divider spacing (bordered sections like "Other Projects", "Related Posts") */
  sectionDivider: {
    /** Container spacing: top margin and padding for bordered sections */
    container: 'mt-8 md:mt-10 lg:mt-12 pt-6 md:pt-7 lg:pt-8',
    /** Heading bottom margin */
    heading: 'mb-4 md:mb-5 lg:mb-6',
    /** Grid gap for card layouts */
    grid: 'gap-4',
  },

  /** Activity feed spacing (content-focused, generous breathing room) */
  activity: {
    /** Spacing between individual threads (generous for visual separation) */
    threadGap: 'space-y-8 md:space-y-10',
    /** Spacing between replies within a thread (comfortable but connected) */
    replyGap: 'space-y-4',
    /** Spacing within activity content (title, description, metadata) */
    contentGap: 'space-y-3',
    /** Spacing between action buttons (like, bookmark, share) */
    actionGap: 'gap-6',
  },

  /**
   * @deprecated Use SPACING_SCALE instead
   * Numeric properties for template literals - will be removed in future version
   *
   * Migration:
   * - Old: `gap-${SPACING.md}` → New: `gap-${spacing('md')}`
   * - Or use direct values: `gap-4` (if static)
   */
  xs: '2',
  /** @deprecated Use SPACING_SCALE.sm or spacing('sm') */
  sm: '3',
  /** @deprecated Use SPACING_SCALE.md or spacing('md') */
  md: '4',
  /** @deprecated Use SPACING_SCALE.lg or spacing('lg') */
  lg: '6',
  /** @deprecated Use SPACING_SCALE.xl or spacing('xl') */
  xl: '8',
  /** @deprecated Use SPACING_SCALE['2xl'] or spacing('2xl') */
  '2xl': '10',
  /** @deprecated Use SPACING_SCALE['1.5'] or spacing('1.5') */
  '1.5': '3',
  /** @deprecated Use SPACING_SCALE['0.5'] or spacing('0.5') */
  '0.5': '1',
} as const;

// ============================================================================
// NUMERIC SPACING (for padding, gaps, margins - NOT space-y)
// ============================================================================

/**
 * @deprecated Use SPACING_SCALE instead
 * Legacy spacing values for backwards compatibility
 */
export const SPACING_VALUES = {
  xs: '2', // 0.5rem
  sm: '3', // 0.75rem
  md: '4', // 1rem
  lg: '6', // 1.5rem
  xl: '8', // 2rem
} as const;

/**
 * Spacing Scale - Type-safe numeric spacing values
 *
 * Use this for template literals, calculations, and dynamic spacing.
 * Returns Tailwind spacing scale numbers (not strings).
 *
 * Replaces numeric properties in SPACING (xs, sm, md, lg, xl, 2xl).
 *
 * @example Template literals (type-safe)
 * ```tsx
 * import { spacing } from '@/lib/design-tokens';
 * <div className={`gap-${spacing('md')} p-${spacing('lg')}`}>
 *   {content}
 * </div>
 * ```
 *
 * @example Direct usage
 * ```tsx
 * import { SPACING_SCALE } from '@/lib/design-tokens';
 * const gapSize = SPACING_SCALE.md; // 4
 * ```
 */
export const SPACING_SCALE = {
  /** 0.125rem (2px) - Minimal spacing */
  '0.5': 0.5,
  /** 0.375rem (6px) - Compact spacing */
  '1.5': 1.5,
  /** 0.5rem (8px) - Extra small */
  xs: 2,
  /** 0.75rem (12px) - Small */
  sm: 3,
  /** 1rem (16px) - Medium (base) */
  md: 4,
  /** 1.5rem (24px) - Large */
  lg: 6,
  /** 2rem (32px) - Extra large */
  xl: 8,
  /** 2.5rem (40px) - 2X large */
  '2xl': 10,
} as const;

/**
 * Type-safe spacing helper for template literals
 *
 * Converts SPACING_SCALE keys to Tailwind class values.
 * Use in template literals for dynamic spacing that preserves type safety.
 *
 * @example
 * ```tsx
 * <div className={`gap-${spacing('md')} mb-${spacing('lg')}`}>
 *   Content with consistent spacing
 * </div>
 * ```
 *
 * @param size - Spacing scale key
 * @returns String representation of the spacing value
 */
export function spacing(size: keyof typeof SPACING_SCALE): string {
  return String(SPACING_SCALE[size]);
}

// ============================================================================
// SEMANTIC COLORS
// ============================================================================

/**
 * Semantic color tokens for consistent alert, status, and interactive states
 * All colors automatically handle dark mode via CSS custom properties
 *
 * Philosophy:
 * - Success: Positive outcomes, confirmations, active states (green)
 * - Warning: Cautions, important notices, pending states (amber/yellow)
 * - Error: Errors, destructive actions, critical issues (red)
 * - Info: Informational content, neutral highlights (blue)
 * - Semantic accents: Categorization and theming (cyan, orange, purple, etc.)
 *
 * @example Alert state
 * ```tsx
 * <div className={SEMANTIC_COLORS.alert.critical.container}>
 *   <IconComponent className={SEMANTIC_COLORS.alert.critical.icon} />
 *   <p className={SEMANTIC_COLORS.alert.critical.text}>Error message</p>
 * </div>
 * ```
 *
 * @example Status indicator
 * ```tsx
 * <Badge className={SEMANTIC_COLORS.status.success}>Complete</Badge>
 * ```
 *
 * @example Interactive focus state
 * ```tsx
 * <button className={SEMANTIC_COLORS.interactive.focus}>Button</button>
 * ```
 */
export const SEMANTIC_COLORS = {
  /** Alert/notification state colors */
  alert: {
    critical: {
      border: 'border-l-4 border-l-error',
      container: 'bg-error-subtle',
      text: 'text-error',
      icon: 'text-error-dark dark:text-error-light',
      label: 'text-error',
    },
    warning: {
      border: 'border-l-4 border-l-warning',
      container: 'bg-warning-subtle',
      text: 'text-warning-foreground',
      icon: 'text-warning-dark dark:text-warning-light',
      label: 'text-warning-dark dark:text-warning-light',
    },
    info: {
      border: 'border-l-4 border-l-info',
      container: 'bg-info-subtle',
      text: 'text-info',
      icon: 'text-info-dark dark:text-info-light',
      label: 'text-info',
    },
    success: {
      border: 'border-l-4 border-l-success',
      container: 'bg-success-subtle',
      text: 'text-success',
      icon: 'text-success-dark dark:text-success-light',
      label: 'text-success',
    },
    notice: {
      border: 'border-l-4 border-l-muted-foreground/30',
      container: 'bg-muted/30 dark:bg-muted/20',
      text: 'text-foreground',
      icon: 'text-muted-foreground',
      label: 'text-foreground',
    },
  },

  /** Status indicators (analytics, progress, metrics) */
  status: {
    success: 'bg-success text-success-foreground',
    warning: 'bg-warning text-warning-foreground',
    info: 'bg-info text-info-foreground',
    neutral: 'bg-muted text-muted-foreground dark:bg-muted/50',
    inProgress: 'bg-warning text-warning-foreground',
    error: 'bg-error text-error-foreground',
  },

  /** Activity feed interaction states (content-focused, subtle actions) */
  activity: {
    action: {
      /** Default state - subtle, low contrast (appears on hover) */
      default: 'text-muted-foreground/60 hover:text-muted-foreground',
      /** Active state - full contrast when focused/hovered */
      active: 'text-foreground hover:text-foreground/80',
      /** Liked state - warm red color */
      liked: 'text-error dark:text-error-light',
      /** Bookmarked state - warm amber color */
      bookmarked: 'text-warning dark:text-warning-light',
    },
  },

  /** Chart colors (map to CSS custom properties) */
  chart: {
    primary: 'bg-chart-1 text-chart-1',
    secondary: 'bg-chart-2 text-chart-2',
    tertiary: 'bg-chart-3 text-chart-3',
    quaternary: 'bg-chart-4 text-chart-4',
    quinary: 'bg-chart-5 text-chart-5',
  },

  /** Interactive element states */
  interactive: {
    hover: 'hover:bg-muted/50 dark:hover:bg-muted/30',
    active: 'active:bg-muted dark:active:bg-muted/40',
    focus:
      'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:focus-visible:ring-offset-background',
    disabled: 'disabled:pointer-events-none disabled:opacity-50 dark:disabled:opacity-40',
  },

  /** Content highlighting and marks */
  highlight: {
    /**
     * Search hits and other text highlights take the visitor's cut, like
     * ::selection and <mark> in globals.css: --foreground on accent-400
     * (light) or accent-700 (dark), 4.5:1 or better in every cut.
     */
    primary: 'bg-accent-400 text-foreground dark:bg-accent-700',
    mark: 'bg-primary/10 dark:bg-primary/20 text-foreground',
    muted: 'bg-muted/50 dark:bg-muted/30 text-muted-foreground',
  },

  /**
   * Semantic accent colors for categorization
   *
   * Consistent structure per hue:
   * - badge: Background + text + border for badge components
   * - text: Text color only
   * - bg: Background color only
   *
   * Hue values are the --semantic-* contract variables in globals.css;
   * surface tokens come from the phosphor identity package.
   *
   * Color groups:
   * - Blues: blue, cyan, sky, indigo, teal
   * - Greens: green, emerald, lime
   * - Purples/Pinks: purple, violet, pink, fuchsia, rose
   * - Reds/Oranges: red, orange, amber
   * - Yellows: yellow
   * - Neutrals: slate, neutral
   */
  accent: {
    // Blues
    blue: {
      badge: 'bg-semantic-blue-subtle text-semantic-blue border-semantic-blue/20',
      text: 'text-semantic-blue',
      bg: 'bg-semantic-blue',
    },
    cyan: {
      badge: 'bg-semantic-cyan-subtle text-semantic-cyan border-semantic-cyan/20',
      text: 'text-semantic-cyan',
      bg: 'bg-semantic-cyan',
    },
    sky: {
      badge: 'bg-semantic-sky-subtle text-semantic-sky border-semantic-sky/20',
      text: 'text-semantic-sky',
      bg: 'bg-semantic-sky',
    },
    indigo: {
      badge: 'bg-semantic-indigo-subtle text-semantic-indigo border-semantic-indigo/20',
      text: 'text-semantic-indigo',
      bg: 'bg-semantic-indigo',
    },
    teal: {
      badge: 'bg-semantic-teal-subtle text-semantic-teal border-semantic-teal/20',
      text: 'text-semantic-teal',
      bg: 'bg-semantic-teal',
    },

    // Greens
    green: {
      badge: 'bg-semantic-green-subtle text-semantic-green border-semantic-green/20',
      text: 'text-semantic-green',
      bg: 'bg-semantic-green',
    },
    emerald: {
      badge: 'bg-semantic-emerald-subtle text-semantic-emerald border-semantic-emerald/20',
      text: 'text-semantic-emerald',
      bg: 'bg-semantic-emerald',
    },
    lime: {
      badge: 'bg-semantic-lime-subtle text-semantic-lime border-semantic-lime/20',
      text: 'text-semantic-lime',
      bg: 'bg-semantic-lime',
    },

    // Purples & Pinks
    purple: {
      badge: 'bg-semantic-purple-subtle text-semantic-purple border-semantic-purple/20',
      text: 'text-semantic-purple',
      bg: 'bg-semantic-purple',
    },
    violet: {
      badge: 'bg-semantic-violet-subtle text-semantic-violet border-semantic-violet/20',
      text: 'text-semantic-violet',
      bg: 'bg-semantic-violet',
    },
    pink: {
      badge: 'bg-semantic-pink-subtle text-semantic-pink border-semantic-pink/20',
      text: 'text-semantic-pink',
      bg: 'bg-semantic-pink',
    },
    fuchsia: {
      badge: 'bg-semantic-fuchsia-subtle text-semantic-fuchsia border-semantic-fuchsia/20',
      text: 'text-semantic-fuchsia',
      bg: 'bg-semantic-fuchsia',
    },
    rose: {
      badge: 'bg-semantic-rose-subtle text-semantic-rose border-semantic-rose/20',
      text: 'text-semantic-rose',
      bg: 'bg-semantic-rose',
    },

    // Reds & Oranges
    red: {
      badge: 'bg-semantic-red-subtle text-semantic-red border-semantic-red/20',
      text: 'text-semantic-red',
      bg: 'bg-semantic-red',
    },
    orange: {
      badge: 'bg-semantic-orange-subtle text-semantic-orange border-semantic-orange/20',
      text: 'text-semantic-orange',
      bg: 'bg-semantic-orange',
    },
    amber: {
      badge: 'bg-semantic-amber-subtle text-semantic-amber border-semantic-amber/20',
      text: 'text-semantic-amber',
      bg: 'bg-semantic-amber',
    },

    // Yellows
    yellow: {
      badge: 'bg-semantic-yellow-subtle text-semantic-yellow border-semantic-yellow/20',
      text: 'text-semantic-yellow',
      bg: 'bg-semantic-yellow',
    },

    // Neutrals & Grays
    slate: {
      badge: 'bg-semantic-slate-subtle text-semantic-slate border-semantic-slate/20',
      text: 'text-semantic-slate',
      bg: 'bg-semantic-slate',
    },
    neutral: {
      badge: 'bg-semantic-neutral-subtle text-semantic-neutral border-semantic-neutral/20',
      text: 'text-semantic-neutral',
      bg: 'bg-semantic-neutral',
    },
  },

  /**
   * Code syntax highlighting tokens
   *
   * Thin wrappers over the text-syntax-* utilities (--syntax-* variables in
   * globals.css, obsidian anchors): keyword/variable/class blue, string/number
   * green, function/attribute amber, comment gray (italic), operator and
   * punctuation near-foreground, tag red. The .dark variable overrides swap
   * schemes; Shiki blocks use the same anchors (src/lib/shiki-obsidian.ts).
   */
  syntax: {
    keyword: 'text-syntax-keyword',
    string: 'text-syntax-string',
    function: 'text-syntax-function',
    comment: 'text-syntax-comment italic',
    variable: 'text-syntax-variable',
    operator: 'text-syntax-operator',
    constant: 'text-syntax-constant',
    class: 'text-syntax-class',
    number: 'text-syntax-number',
    punctuation: 'text-syntax-punctuation',
    tag: 'text-syntax-tag',
    attribute: 'text-syntax-attribute',
  },
} as const;

// ============================================================================
// SERIES COLORS
// ============================================================================

/**
 * Series color palette for blog series theming
 *
 * Provides semantic color schemes for series badges, cards, and navigation.
 * All colors are optimized for light/dark mode with accessible contrast ratios.
 *
 * Color Selection Philosophy:
 * - Maps to semantic color categories (success, warning, error, info, accents)
 * - Semantic names tied to common series themes (tutorial, security, performance, etc.)
 * - WCAG AA contrast compliance for text on backgrounds
 * - Consistent with existing design token system
 *
 * @example
 * ```tsx
 * // Using series colors in components
 * <Badge className={SERIES_COLORS.tutorial.badge}>
 *   Tutorial Series
 * </Badge>
 *
 * <Card className={SERIES_COLORS.security.card}>
 *   {content}
 * </Card>
 * ```
 */
export const SERIES_COLORS = {
  /** Default series color (primary brand) */
  default: {
    /** Badge background/text */
    badge: 'bg-primary/10 text-primary border-primary/20',
    /** Card accent (border, highlights) */
    card: 'border-primary/20 hover:border-primary/40',
    /** Icon color */
    icon: 'text-primary',
  },

  /** Tutorial/educational series (blue → info) */
  tutorial: {
    badge: SEMANTIC_COLORS.accent.sky.badge,
    card: 'border-semantic-sky/20 hover:border-semantic-sky/40',
    icon: SEMANTIC_COLORS.accent.sky.text,
  },

  /** Security/hardening series (shield theme - cyan) */
  security: {
    badge: SEMANTIC_COLORS.accent.cyan.badge,
    card: 'border-semantic-cyan/20 hover:border-semantic-cyan/40',
    icon: SEMANTIC_COLORS.accent.cyan.text,
  },

  /** Performance/optimization series (lightning theme - orange) */
  performance: {
    badge: SEMANTIC_COLORS.accent.orange.badge,
    card: 'border-semantic-orange/20 hover:border-semantic-orange/40',
    icon: SEMANTIC_COLORS.accent.orange.text,
  },

  /** Architecture/design series (violet) */
  architecture: {
    badge: SEMANTIC_COLORS.accent.violet.badge,
    card: 'border-semantic-violet/20 hover:border-semantic-violet/40',
    icon: SEMANTIC_COLORS.accent.violet.text,
  },

  /** Development/coding series (emerald) */
  development: {
    badge: SEMANTIC_COLORS.accent.emerald.badge,
    card: 'border-semantic-emerald/20 hover:border-semantic-emerald/40',
    icon: SEMANTIC_COLORS.accent.emerald.text,
  },

  /** Testing/QA series (green → success) */
  testing: {
    badge: 'bg-success-subtle text-success border-success/20',
    card: 'border-success/20 hover:border-success/40',
    icon: 'text-success',
  },

  /** DevOps/deployment series (sky) */
  devops: {
    badge: SEMANTIC_COLORS.accent.sky.badge,
    card: 'border-semantic-sky/20 hover:border-semantic-sky/40',
    icon: SEMANTIC_COLORS.accent.sky.text,
  },

  /** Career/soft skills series (warning → amber) */
  career: {
    badge: 'bg-warning-subtle text-warning-dark dark:text-warning-light border-warning/20',
    card: 'border-warning/20 hover:border-warning/40',
    icon: 'text-warning-dark dark:text-warning-light',
  },

  /** Deep dive/advanced series (indigo) */
  advanced: {
    badge: SEMANTIC_COLORS.accent.indigo.badge,
    card: 'border-semantic-indigo/20 hover:border-semantic-indigo/40',
    icon: SEMANTIC_COLORS.accent.indigo.text,
  },

  /** Design/UI/UX series (pink) */
  design: {
    badge: SEMANTIC_COLORS.accent.pink.badge,
    card: 'border-semantic-pink/20 hover:border-semantic-pink/40',
    icon: SEMANTIC_COLORS.accent.pink.text,
  },

  /** Quick tips/snippets series (lime) */
  tips: {
    badge: SEMANTIC_COLORS.accent.lime.badge,
    card: 'border-semantic-lime/20 hover:border-semantic-lime/40',
    icon: SEMANTIC_COLORS.accent.lime.text,
  },

  /** Troubleshooting/debugging series (error → red) */
  debugging: {
    badge: 'bg-error-subtle text-error border-error/20',
    card: 'border-error/20 hover:border-error/40',
    icon: 'text-error',
  },

  /** Neutral/general series (muted) */
  general: {
    badge: 'bg-muted/50 text-muted-foreground border-border',
    card: 'border-border hover:border-muted-foreground/40',
    icon: 'text-muted-foreground',
  },
} as const;

/**
 * Valid series color theme names
 */
export type SeriesColorTheme = keyof typeof SERIES_COLORS;

/**
 * Get series color configuration by theme name
 * Falls back to 'default' if theme not found
 *
 * @param theme - Series color theme name
 * @returns Series color configuration object
 *
 * @example
 * ```tsx
 * const colors = getSeriesColors('security');
 * <Badge className={colors.badge}>Security Series</Badge>
 * ```
 */
export function getSeriesColors(theme: string = 'default') {
  return SERIES_COLORS[theme as SeriesColorTheme] || SERIES_COLORS.default;
}

// ============================================================================
// HOVER EFFECTS
// ============================================================================

/**
 * Hover effect patterns for interactive elements
 * Ensures consistent feedback across different component types
 *
 * **Decision Guide:**
 * - **80% use case:** `card` - Standard interactive cards, posts, projects
 * - **15% use case:** `cardSubtle` - Less prominent secondary cards
 * - **5% use case:** `button` or `link` - Specific interactive elements
 * - **Rare:** `cardGlow`, `cardTilt` - Special effects only
 *
 * All hover effects include:
 * - Smooth transitions (300ms for cards, 200ms for minimal)
 * - Shadow elevation changes
 * - Optional transforms (lift, scale)
 * - Active state feedback (scale-down for tactile feel)
 *
 * @example Standard card hover (most common - 80% of use cases)
 * ```tsx
 * import { Card } from '@/components/ui/card'
 * import { HOVER_EFFECTS } from '@/lib/design-tokens'
 *
 * <Card className={HOVER_EFFECTS.card}>
 *   <CardHeader>
 *     <CardTitle>Project Title</CardTitle>
 *   </CardHeader>
 *   <CardContent>
 *     Project details...
 *   </CardContent>
 * </Card>
 * ```
 *
 * @example Interactive link
 * ```tsx
 * <Link href="/blog" className={HOVER_EFFECTS.link}>
 *   Read more →
 * </Link>
 * ```
 *
 * @example Button with feedback
 * ```tsx
 * <button className={cn('px-6 py-3 rounded-md', HOVER_EFFECTS.button)}>
 *   Click me
 * </button>
 * ```
 *
 * @see docs/design/ANIMATION_SYSTEM_ANALYSIS.md for complete animation guidelines
 */
export const HOVER_EFFECTS = {
  /**
   * Standard card hover (DEFAULT - use for 80% of interactive cards)
   *
   * Use for: Blog posts, projects, content cards, archive items
   * Effect: Lift (0.5px), shadow increase, border highlight, press feedback
   */
  card: 'transition-all duration-300 hover:shadow-lg hover:border-primary/30 hover:-translate-y-0.5 active:scale-[0.98] active:shadow-md',

  /**
   * Subtle hover for secondary/inline cards
   *
   * Use for: Sidebar cards, inline content, less prominent elements
   * Effect: Smaller lift (0.5px), medium shadow, press feedback
   */
  cardSubtle:
    'transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]',

  /**
   * Featured/hero cards (already prominent elements)
   *
   * Use for: Hero sections, featured content, primary CTAs
   * Effect: Lift (0.5px), extra-large shadow, border highlight, press feedback
   */
  cardFeatured:
    'transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 active:scale-[0.98] active:shadow-md',

  /**
   * Minimal hover for tertiary/subtle cards
   *
   * Use for: Lightweight interactions, nested cards, minimal UI
   * Effect: Shadow only (no transform), faster transition (200ms)
   */
  cardMinimal: 'transition-shadow duration-200 hover:shadow-md',

  /**
   * Lift effect on hover (larger upward movement)
   *
   * Use for: Special emphasis cards, gallery items
   * Effect: Larger lift (1px), shadow increase
   */
  cardLift: 'transition-all duration-300 hover:shadow-lg hover:-translate-y-1',

  /**
   * Interactive buttons (FABs, CTAs)
   *
   * Use for: Action buttons, floating action buttons
   * Effect: Shadow increase, press scale-down feedback
   */
  button: 'transition-shadow hover:shadow-xl active:scale-95 active:shadow-lg',

  /**
   * Text links
   *
   * Use for: Inline links, text-based navigation
   * Effect: Underline on hover, color transition, opacity feedback on press
   */
  link: 'hover:underline underline-offset-4 decoration-skip-ink-none will-change-auto transition-theme active:opacity-70',

  /**
   * Card glow effect on hover (SPECIALTY - use sparingly)
   *
   * Use for: Premium content, special highlights, attention-grabbing elements
   * Effect: Glow shadow (custom shadow-glow utility)
   */
  cardGlow: 'transition-all duration-300 hover:shadow-glow',

  /**
   * Card tilt effect on hover (SPECIALTY - use sparingly)
   *
   * Use for: Playful interactions, creative portfolios, 3D effects
   * Effect: Slight rotation (1deg) + scale (1.02x)
   */
  cardTilt: 'transition-transform duration-300 hover:rotate-1 hover:scale-[1.02]',
} as const;

// ============================================================================
// ANIMATIONS
// ============================================================================

/**
 * Animation System - Performance-First, CSS-Native
 *
 * Philosophy:
 * 1. CSS handles everything - no JavaScript animation libraries needed
 * 2. Use transform + opacity only (GPU-accelerated, 60fps)
 * 3. CSS handles reduced-motion globally via @media query
 * 4. Simple utility classes for common patterns
 *
 * The CSS custom properties are defined in globals.css under @theme inline.
 * These TypeScript constants reference those values for documentation.
 *
 * @example
 * ```tsx
 * // Scroll reveal with stagger (CSS classes)
 * {items.map((item, i) => (
 *   <div
 *     key={item.id}
 *     className={cn(
 *       ANIMATION.reveal.base,
 *       ANIMATION.reveal.up,
 *       isVisible && ANIMATION.reveal.visible,
 *       `stagger-${i + 1}`
 *     )}
 *   >
 *     {item.content}
 *   </div>
 * ))}
 *
 * // Simple hover effect
 * <Card className={ANIMATION.hover.lift}>...</Card>
 *
 * // Color transition (theme changes)
 * <div className={ANIMATION.transition.colors}>...</div>
 * ```
 *
 * @see src/app/globals.css for CSS custom properties and utility classes
 */
export const ANIMATION = {
  /** Duration scale (references CSS custom properties) */
  duration: {
    instant: 'duration-[0ms]', // --duration-instant (0ms) - No animation
    fast: 'duration-[150ms]', // --duration-fast (150ms) - Quick interactions
    normal: 'duration-[300ms]', // --duration-normal (300ms) - Standard transitions
    slow: 'duration-[500ms]', // --duration-slow (500ms) - Complex animations
  },

  /** Easing functions for animation curves */
  easing: {
    default: 'ease', // Default cubic-bezier
    in: 'ease-in', // Slow start
    out: 'ease-out', // Slow end
    inOut: 'ease-in-out', // Slow start and end
  },

  /** Transition utilities - Performance optimized */
  transition: {
    /** Base (opacity + transform, 300ms) - Default choice */
    base: 'transition-base',
    /** Fast variant (opacity + transform, 150ms) */
    fast: 'transition-fast',
    /** Slow variant (opacity + transform, 500ms) */
    slow: 'transition-slow',
    /** Movement only (transform, 150ms) - Best for hover/interactive */
    movement: 'transition-movement',
    /** Appearance (opacity + transform, 300ms) - Best for reveals */
    appearance: 'transition-appearance',
    /** Theme (colors, 150ms) - Best for theme changes/hover states */
    theme: 'transition-theme',
  },

  /** Scroll-reveal animation classes */
  reveal: {
    /** Initial hidden state - add this by default */
    hidden: 'reveal-hidden',
    /** Visible state - add when element enters viewport */
    visible: 'reveal-visible',
    /** Direction variants (combine with hidden/visible) */
    up: 'reveal-up',
    down: 'reveal-down',
    left: 'reveal-left',
    right: 'reveal-right',
    scale: 'reveal-scale',
  },

  /** Hover effects */
  hover: {
    lift: 'hover-lift', // Subtle lift on hover
  },

  /** Interactive feedback */
  interactive: {
    press: 'press-effect', // Scale down on active/press
  },

  /** Stagger delays for lists (50ms increments) */
  stagger: {
    1: 'stagger-1',
    2: 'stagger-2',
    3: 'stagger-3',
    4: 'stagger-4',
    5: 'stagger-5',
    6: 'stagger-6',
  },

  /** Activity feed engagement animations */
  activity: {
    /** Like button interaction (scale down on click, smooth transition) */
    like: 'transition-all duration-200 active:scale-95',
    /** Pulse animation (one-time pulse effect for reactions) */
    pulse: 'animate-pulse-once',
    /** Count increment (scale up briefly when count changes) */
    countIncrement: 'transition-transform duration-300 scale-110',
  },

  /** Homepage visual effect animations */
  effects: {
    /** Count-up animation for number reveals */
    countUp: 'animate-count-up',
    /** Shimmer effect for loading and polish */
    shimmer: 'animate-shimmer',
    /** Subtle pulse for interactive elements */
    pulse: 'animate-pulse-subtle',
    /** Floating animation for parallax elements */
    float: 'animate-float',
  },
} as const;

/**
 * Animation Timing & Easing Constants
 *
 * Raw CSS values for inline style animations.
 * Use these constants when you need to access raw duration/easing values
 * in JavaScript (e.g., for Framer Motion, inline styles, or calculations).
 *
 * For className-based animations, prefer ANIMATION instead.
 *
 * All animations respect `prefers-reduced-motion` via globals.css.
 *
 * Usage:
 * ```tsx
 * // In component inline styles
 * style={{ animation: ANIMATION_CONSTANTS.types.fadeIn }}
 *
 * // For transitions
 * style={{ transition: ANIMATION_CONSTANTS.transition.colors }}
 *
 * // With stagger effect
 * style={{ animationDelay: `${ANIMATION_CONSTANTS.stagger.normal * index}ms` }}
 * ```
 *
 * @see ANIMATION - For CSS class-based animations (preferred)
 * @see /src/app/globals.css - Animation keyframes and reduced-motion support
 */
export const ANIMATION_CONSTANTS = {
  /**
   * Animation durations
   * Based on Material Design motion guidelines
   */
  duration: {
    /** Instant feedback (150ms) - Micro-interactions, hovers */
    instant: '150ms',

    /** Fast response (300ms) - UI state changes, tooltips, dropdowns */
    fast: '300ms',

    /** Normal transition (500ms) - Content reveals, page transitions */
    normal: '500ms',

    /** Slow ambient (2s) - Background animations, shimmer effects */
    slow: '2s',

    /** Extended ambient (3s) - Floating animations, parallax */
    extended: '3s',
  },

  /**
   * Easing functions
   * Matches Tailwind's built-in easing curves
   */
  easing: {
    /** Default easing - Most transitions */
    default: 'cubic-bezier(0.4, 0, 0.2, 1)', // ease-out

    /** Smooth easing - Organic movement */
    smooth: 'ease-in-out',

    /** Bounce easing - Playful interactions */
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',

    /** Linear - Constant speed (shimmer, loading bars) */
    linear: 'linear',

    /** Sharp entry - Emphasize arrival */
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)', // ease-in-out but sharper
  },

  /**
   * Pre-composed animation strings
   * Ready to use with CSS `animation` property
   */
  types: {
    /** Shimmer effect for skeleton loaders (2s linear infinite) */
    shimmer: 'shimmer 2s linear infinite',

    /** Fade in content (500ms ease-out) */
    fadeIn: 'fade-in 0.5s ease-out forwards',

    /** Fade in with upward slide (500ms ease-out) */
    fadeInUp: 'fade-in-up 0.5s ease-out forwards',

    /** Single pulse effect (300ms ease-in-out) */
    pulseOnce: 'pulse-once 300ms ease-in-out',

    /** Subtle continuous pulse (2s ease-in-out infinite) */
    pulseSubtle: 'pulse-subtle 2s ease-in-out infinite',

    /** Floating parallax effect (3s ease-in-out infinite) */
    float: 'float 3s ease-in-out infinite',

    /** Highlight flash for TOC navigation (2s ease-in-out) */
    highlightPulse: 'highlight-pulse 2s ease-in-out',

    /** Count-up number reveal (500ms ease-out) */
    countUp: 'count-up 500ms ease-out forwards',

    /** Loading bar progress (500ms ease-in-out) */
    loadingBar: 'loading-bar 0.5s ease-in-out forwards',
  },

  /**
   * Transition utilities
   * For use with Tailwind's transition classes
   */
  transition: {
    /** All properties with normal duration */
    all: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)',

    /** Colors only (backgrounds, borders, text) */
    colors:
      'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), border-color 150ms cubic-bezier(0.4, 0, 0.2, 1), color 150ms cubic-bezier(0.4, 0, 0.2, 1)',

    /** Transform only (scale, translate, rotate) */
    transform: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',

    /** Opacity only */
    opacity: 'opacity 200ms ease',

    /** Box shadow (for hover effects) */
    shadow: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  },

  /**
   * Stagger delays for list animations
   * Multiply by index: `style={{ animationDelay: `${ANIMATIONS.stagger.normal * index}ms` }}`
   */
  stagger: {
    /** 50ms between items - Fast reveal */
    fast: 50,

    /** 100ms between items - Standard reveal */
    normal: 100,

    /** 150ms between items - Slow reveal */
    slow: 150,
  },
} as const;

// ============================================================================
// BORDERS & SHADOWS
// ============================================================================

/**
 * Border radius patterns for consistent rounded corners
 */
export const BORDERS = {
  /** Default card/component border radius */
  card: 'rounded-lg',

  /** Button border radius */
  button: 'rounded-md',

  /** Input border radius */
  input: 'rounded-md',

  /** Badge border radius */
  badge: 'rounded-md',

  /** Circular elements (avatars, FABs) */
  circle: 'rounded-full',

  /** Dialog/modal border radius */
  dialog: 'rounded-lg',

  /** Dropdown/popover border radius */
  dropdown: 'rounded-md',

  /** Larger containers (hero sections, containers) */
  container: 'rounded-xl',
} as const;

/**
 * Shadow elevation patterns
 *
 * Includes both the 3-tier content hierarchy system and semantic UI shadows.
 *
 * @example
 * ```tsx
 * // Content hierarchy (code blocks, tables, alerts)
 * <pre className={SHADOWS.tier1.combined}>...</pre>
 *
 * // Semantic UI shadows
 * <Card className={SHADOWS.card.rest}>
 *   {content}
 * </Card>
 * ```
 */
export const SHADOWS = {
  /** Tier 1: Reference content - Code blocks, important callouts (most prominent) */
  tier1: {
    /** Light mode shadow */
    light: 'shadow-[0_2px_8px_rgb(0_0_0_/_0.12)]',
    /** Dark mode shadow */
    dark: 'dark:shadow-[0_2px_8px_rgb(0_0_0_/_0.3)]',
    /** Combined (light + dark) */
    combined: 'shadow-[0_2px_8px_rgb(0_0_0_/_0.12)] dark:shadow-[0_2px_8px_rgb(0_0_0_/_0.3)]',
  },

  /** Tier 2: Data content - Tables, structured content (medium prominence) */
  tier2: {
    /** Light mode shadow */
    light: 'shadow-[0_1px_4px_rgb(0_0_0_/_0.08)]',
    /** Dark mode shadow */
    dark: 'dark:shadow-[0_1px_4px_rgb(0_0_0_/_0.2)]',
    /** Combined (light + dark) */
    combined: 'shadow-[0_1px_4px_rgb(0_0_0_/_0.08)] dark:shadow-[0_1px_4px_rgb(0_0_0_/_0.2)]',
    /** Hover state for interactive depth */
    hover:
      'hover:shadow-[0_4px_12px_rgb(0_0_0_/_0.12)] dark:hover:shadow-[0_4px_12px_rgb(0_0_0_/_0.25)]',
  },

  /** Tier 3: Inline/embedded content - Alert banners, context clues (subtle) */
  tier3: {
    /** Light mode shadow */
    light: 'shadow-[0_1px_2px_rgb(0_0_0_/_0.05)]',
    /** Dark mode shadow */
    dark: 'dark:shadow-[0_1px_2px_rgb(0_0_0_/_0.15)]',
    /** Combined (light + dark) */
    combined: 'shadow-[0_1px_2px_rgb(0_0_0_/_0.05)] dark:shadow-[0_1px_2px_rgb(0_0_0_/_0.15)]',
  },

  /** Subtle elevation (cards at rest) */
  sm: 'shadow-sm',

  /** Standard elevation (hover states) */
  md: 'shadow-md',

  /** High elevation (focused elements, modals) */
  lg: 'shadow-lg',

  /** Maximum elevation (floating action buttons) */
  xl: 'shadow-xl',

  /** Extra shadow (overlay, stacked elements) */
  '2xl': 'shadow-2xl',

  /** Semantic card shadows */
  card: {
    rest: 'shadow-sm',
    hover: 'shadow-md',
    active: 'shadow-lg',
  },

  /** Dropdown/popover shadows */
  dropdown: 'shadow-lg',

  /** Modal shadows */
  modal: 'shadow-xl',

  /** FAB shadows */
  fab: 'shadow-xl',

  /** No shadow */
  none: 'shadow-none',
} as const;

/**
 * Touch target sizes and button sizing for mobile accessibility
 *
 * Following Apple HIG and Material Design guidelines:
 * - Minimum touch target: 44x44px (accessible for most users)
 * - Comfortable spacing: 8px minimum between targets
 * - Mobile-first: All buttons 44px+ on mobile, can reduce on desktop with md: prefix
 *
 * @example
 * ```tsx
 * // Icon button (mobile first)
 * <button className={TOUCH_TARGET.iconMobile}>
 *   <Icon />
 * </button>
 *
 * // Text button (mobile first)
 * <button className={TOUCH_TARGET.textMobile}>
 *   Action
 * </button>
 *
 * // Responsive icon (44px mobile, 36px tablet+)
 * <button className="h-11 w-11 md:h-9 md:w-9">
 *   <Icon />
 * </button>
 * ```
 */
export const TOUCH_TARGET = {
  // ========== Size Standards ==========
  /** Minimum touch target (44x44px per WCAG AAA) */
  minimum: '44px',

  /** Comfortable touch target (48x48px) */
  comfortable: '48px',

  /** Large touch target for thumbs (56x56px) */
  large: '56px',

  /** Minimum spacing between targets (8px) */
  spacing: '8px',

  // ========== Mobile Icon Buttons (default) ==========
  /** Icon-only button (44x44px) - matches min touch target */
  iconMobile: 'h-11 w-11',

  /** Small icon button (36x36px) - for dense layouts, use with caution */
  iconSmall: 'h-9 w-9',

  // ========== Mobile Text Buttons ==========
  /** Standard text button (44px height with padding) */
  textMobile: 'h-11 px-4',

  /** Large text button (48px height) */
  textLarge: 'h-12 px-5',

  /** Small text button (36px height) - use sparingly */
  textSmall: 'h-9 px-3',

  // ========== Combined Button Sizes ==========
  /** Standard button/link size (square) */
  standard: 'h-12 w-12',

  /** Minimum recommended size (44x44px) */
  min: 'min-h-11 min-w-11',

  // ========== Desktop Responsive Sizing ==========
  /** Icon button: 44px mobile, 36px tablet+, 32px desktop+ */
  iconResponsive: 'h-11 w-11 md:h-9 md:w-9 lg:h-8 lg:w-8',

  /** Text button: 44px mobile, 40px tablet+, 36px desktop+ */
  textResponsive: 'h-11 md:h-10 lg:h-9 px-4 md:px-3 lg:px-2',

  // ========== Action Group Spacing ==========
  /** Spacing between action buttons in a group (like, reply, share) */
  actionGroupGap: 'gap-2 md:gap-1',

  // ========== Special Cases ==========
  /** FAB (floating action button) - always prominent */
  fab: 'h-14 w-14',

  /** Mobile menu toggle button */
  menuToggle: 'h-11 w-11',

  /** Close/dismiss button */
  close: 'h-11 w-11 md:h-9 md:w-9',
} as const;

// ============================================================================
// Z-INDEX HIERARCHY
// ============================================================================

/**
 * Centralized z-index tokens for consistent stacking context management.
 *
 * Layers are organized from lowest to highest:
 * - background: Below normal content (decorative elements)
 * - content: Normal content layer (cards, sections)
 * - elevated: Slightly raised elements (sticky sidebars, TOC)
 * - sticky: Sticky headers, progress bars
 * - header: Fixed site header and navigation
 * - dropdown: Dropdown menus, popovers, tooltips
 * - overlay: Modal backdrops, image overlays
 * - modal: Modal dialogs, command palette
 * - toast: Toast notifications (highest layer)
 *
 * @example
 * ```tsx
 * import { Z_INDEX } from '@/lib/design-tokens';
 *
 * <header className={`fixed top-0 ${Z_INDEX.header}`}>
 *   <nav>...</nav>
 * </header>
 *
 * <div className={`fixed inset-0 ${Z_INDEX.overlay}`}>
 *   <Dialog className={Z_INDEX.modal}>...</Dialog>
 * </div>
 * ```
 */
export const Z_INDEX = {
  /** Background decorative elements (z-[-1]) */
  background: 'z-[-1]',
  /** Normal content stacking (z-0) */
  content: 'z-0',
  /** Slightly elevated elements like sticky sidebars, TOC (z-10) */
  elevated: 'z-10',
  /** Sticky elements like reading progress, sub-navigation (z-30) */
  sticky: 'z-30',
  /** Fixed site header, bottom nav, FABs (z-40) */
  header: 'z-40',
  /** Dropdowns, popovers, tooltips above navigation (z-50) */
  dropdown: 'z-50',
  /** Modal backdrops, image lightbox overlays (z-60) */
  overlay: 'z-60',
  /** Modal dialogs, command palette, sheets (z-70) */
  modal: 'z-70',
  /** Toast notifications - always on top (z-80) */
  toast: 'z-80',
} as const;

// ============================================================================
// FOCUS RING PATTERNS
// ============================================================================

/**
 * Standardized focus ring patterns for accessible keyboard navigation.
 * All interactive elements should use these tokens for consistent focus indication.
 *
 * Uses `focus-visible` (not `focus`) to only show rings on keyboard navigation,
 * avoiding unwanted rings on mouse clicks.
 *
 * @example
 * ```tsx
 * import { FOCUS_RING } from '@/lib/design-tokens';
 *
 * <button className={`px-4 py-2 ${FOCUS_RING.default}`}>Click me</button>
 * <a className={FOCUS_RING.within}>Link with focus ring</a>
 * <input className={FOCUS_RING.inset} />
 * ```
 */
export const FOCUS_RING = {
  /** Standard focus ring - use on buttons, links, interactive cards
   * Ring: 2px, color: semantic ring color, offset: 2px */
  default:
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  /** Inset focus ring (no offset) - use on inputs, textareas, selects */
  inset: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  /** Focus-within ring - use on wrapper elements that contain focusable children */
  within:
    'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
  /** Subtle focus ring (1px) - use in dense UI like tables, lists */
  subtle:
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1',
  /** Inverted focus ring for dark backgrounds - use on overlays, dark cards */
  inverted:
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/90',
} as const;

// ============================================================================
// SCROLL OFFSET
// ============================================================================

/**
 * Scroll offset tokens for proper anchor scroll positioning under fixed headers.
 * Uses a CSS variable `--header-height` for consistency with the actual header height.
 *
 * @example
 * ```tsx
 * import { SCROLL_OFFSET } from '@/lib/design-tokens';
 * <h2 id="section" className={SCROLL_OFFSET.heading}>Section Title</h2>
 * ```
 */
export const SCROLL_OFFSET = {
  /** Scroll margin for headings/anchors under fixed header (80px = h-20) */
  heading: 'scroll-mt-20',
  /** Scroll margin for sections with extra breathing room (96px = h-24) */
  section: 'scroll-mt-24',
} as const;

// ============================================================================
// PAGE LAYOUT PATTERNS
// ============================================================================

/**
 * Page-level layout patterns for core pages (/, /about, /contact, /resume)
 * Ensures consistent structure and spacing across all main pages
 *
 * @example
 * ```tsx
 * <div className={PAGE_LAYOUT.wrapper}>
 *   <PageHero />
 *   <PageSection>{content}</PageSection>
 * </div>
 * ```
 */
export const PAGE_LAYOUT = {
  /** Root page wrapper - provides consistent vertical rhythm and ensures backgrounds render fully */
  wrapper: 'min-h-full overflow-visible',

  /** Unified hero section - used for all hero sections across the site
   * The fixed header's height is reserved by an in-flow spacer (SiteHeader),
   * so hero padding is breathing room only
   * Works for both standard pages and archive/listing pages
   */
  hero: {
    /** Standard container - used for most pages */
    container: `mx-auto ${CONTAINER_WIDTHS.standard} ${CONTAINER_PADDING}`,
    /** Archive container - used for blog/work/portfolio listings */
    archiveContainer: `mx-auto ${CONTAINER_WIDTHS.archive} ${CONTAINER_PADDING}`,
    /** Unified padding for all hero sections */
    padding: 'pt-10 md:pt-14 lg:pt-18 pb-8 md:pb-12',
    /** Hero title + description wrapper */
    content: SPACING.proseHero,
  },

  /** Standard page section spacing */
  section: {
    /** Section container - no vertical padding (handled by parent gap) */
    container: `mx-auto ${CONTAINER_WIDTHS.standard} ${CONTAINER_PADDING}`,
    /** Section content wrapper */
    content: SPACING.subsection,
  },

  /** Prose/reading-optimized section (about page, long-form content) */
  proseSection: {
    /** Container for prose content - optimal reading width (65ch) */
    container: `mx-auto ${CONTAINER_WIDTHS.prose} ${CONTAINER_PADDING}  pt-6 md:pt-8 lg:pt-10 pb-6 md:pb-8 lg:pb-10`,
    /** Prose content wrapper */
    content: SPACING.prose,
  },

  /** Narrow section for forms (contact page) */
  narrowSection: {
    /** Container for narrow content */
    container: `mx-auto ${CONTAINER_WIDTHS.narrow} ${CONTAINER_PADDING}`,
    /** Content wrapper */
    content: SPACING.content,
  },

  /** Article/project page section with fixed header compensation */
  articleSection: {
    /** Container for article content - accounts for fixed header (h-18 = 72px)
     * Provides proper spacing below navigation bar
     * Used for individual article/project pages like /blog/[slug], /work/[slug]
     */
    container: `mx-auto ${CONTAINER_WIDTHS.content} ${CONTAINER_PADDING} pt-10 md:pt-14 lg:pt-18 pb-8 md:pb-12`,
    /** Article content wrapper */
    content: SPACING.subsection,
  },
} as const;

/**
 * Hero section variants for different page types
 */
export const HERO_VARIANTS = {
  /** Standard hero (about, contact, projects) */
  standard: {
    title: TYPOGRAPHY.h1.standard,
    description: TYPOGRAPHY.description,
    spacing: SPACING.proseHero,
  },

  /** Homepage hero (larger, more prominent) */
  homepage: {
    title: TYPOGRAPHY.h1.hero,
    description: TYPOGRAPHY.description,
    spacing: SPACING.proseHero,
  },

  /** Article hero (blog posts) */
  article: {
    title: TYPOGRAPHY.h1.article,
    description: TYPOGRAPHY.description,
    spacing: SPACING.proseHero,
  },
} as const;

// ============================================================================
// SCROLL BEHAVIOR
// ============================================================================

/**
 * Scroll behavior and navigation settings
 *
 * @example
 * ```tsx
 * // Offset for fixed header; scrollBehavior() (@/lib/scroll-behavior) honors reduced motion
 * window.scrollTo({
 *   top: targetY - SCROLL_BEHAVIOR.offset.standard,
 *   behavior: scrollBehavior()
 * });
 * ```
 */
export const SCROLL_BEHAVIOR = {
  /** Scroll behavior modes */
  behavior: {
    smooth: 'smooth' as const,
    instant: 'auto' as const,
  },

  /** Standard scroll offsets for fixed headers */
  offset: {
    /** Standard header height (80px) */
    standard: 80,
    /** Tall header or with announcement bar (100px) */
    tall: 100,
    /** Mobile with bottom nav (104px from bottom + header) */
    mobile: 104,
  },

  /** Scroll thresholds for UI elements */
  threshold: {
    /** Show back-to-top button after ~1.5 viewports */
    backToTop: 400,
    /** Show floating elements after minimal scroll */
    floating: 200,
  },

  /** Scroll snap settings */
  snap: {
    /** Scroll snap type for section navigation */
    type: 'scroll-snap-y scroll-snap-mandatory' as const,
    /** Scroll snap alignment */
    align: 'scroll-snap-start' as const,
  },
} as const;

// ============================================================================
// COMPONENT-SPECIFIC PATTERNS
// ============================================================================

/**
 * Grid layout patterns for consistent multi-column layouts
 *
 * @example
 * ```tsx
 * <div className={GRID_PATTERNS.two}>
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 * </div>
 * ```
 */
export const GRID_PATTERNS = {
  /** Two-column grid (1 col mobile, 2 col tablet+) */
  two: 'grid grid-cols-1 md:grid-cols-2 gap-6',

  /** Three-column grid (1 col mobile, 3 col desktop) */
  three: 'grid grid-cols-1 md:grid-cols-3 gap-6',

  /** Four-column grid (2 cols mobile, 4 cols desktop) */
  four: 'grid grid-cols-2 md:grid-cols-4 gap-4',

  /** Auto-responsive grid (1 → 2 → 3 → 4 columns) */
  auto: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6',
} as const;

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/** Type for container width variants */
export type ContainerWidth = keyof typeof CONTAINER_WIDTHS;

/** Type for heading variants */
export type HeadingVariant = keyof typeof TYPOGRAPHY.h1;

/** Type for hover effect variants */
export type HoverEffect = keyof typeof HOVER_EFFECTS;

/** Type for animation durations */
export type AnimationDuration = keyof typeof ANIMATION_CONSTANTS;

/** Type for semantic color categories */
export type SemanticColorCategory = keyof typeof SEMANTIC_COLORS;

/** Type for alert state variants */
export type AlertVariant = keyof typeof SEMANTIC_COLORS.alert;

/** Type for status variants */
export type StatusVariant = keyof typeof SEMANTIC_COLORS.status;

/** Type for grid layout patterns */
export type GridPattern = keyof typeof GRID_PATTERNS;

// ============================================================================
// IMAGE PLACEHOLDERS
// ============================================================================

/**
 * Standard blur placeholder for Next.js Image components
 * Provides smooth loading experience with subtle gray placeholder
 *
 * Usage:
 * - Add placeholder="blur" to Image component
 * - Add blurDataURL={IMAGE_PLACEHOLDER.blur} for inline images
 * - For external images, consider using plaiceholder library
 *
 * @example
 * ```tsx
 * <Image
 *   src="/images/avatar.jpg"
 *   alt="Avatar"
 *   fill
 *   placeholder="blur"
 *   blurDataURL={IMAGE_PLACEHOLDER.blur}
 * />
 * ```
 */
export const IMAGE_PLACEHOLDER = {
  /** Standard gray blur placeholder (matches muted background) */
  blur: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlNWU3ZWIiLz48L3N2Zz4=',
} as const;

// ============================================================================
// APP-SPECIFIC TOKENS (Interactive Features)
// ============================================================================

/**
 * App-specific design tokens for interactive, app-like features
 *
 * These tokens support progressive web app features, command palette,
 * gestures, and other native-like interactions.
 *
 * @example
 * ```tsx
 * <div className={`transition-all ${APP_TOKENS.ANIMATIONS.pageTransition}`}>
 *   {content}
 * </div>
 * ```
 */
export const APP_TOKENS = {
  /** Gesture interaction thresholds and parameters */
  GESTURES: {
    /** Minimum distance to trigger swipe action */
    swipeThreshold: '50px',
    /** Duration to hold for long-press detection */
    longPressDelay: '500ms',
    /** Maximum time for tap detection */
    tapMaxDuration: '200ms',
    /** Maximum movement allowed for tap */
    tapMaxMovement: '10px',
  },

  /** Animation timing and easing for app interactions */
  ANIMATIONS: {
    /** Page/view transition timing */
    pageTransition: 'duration-200 ease-in-out',
    /** Optimistic UI update feedback */
    optimisticUpdate: 'duration-100 ease-out',
    /** Pull-to-refresh animation */
    pullToRefresh: 'duration-300',
    /** Command palette open/close */
    commandPalette: 'duration-200 ease-in-out',
    /** Modal/dialog transitions */
    modal: 'duration-150 ease-out',
    /** Toast notification entrance */
    toast: 'duration-300 ease-out',
  },

  /** Touch target sizes following accessibility guidelines */
  TOUCH_TARGETS: {
    /** iOS Human Interface Guidelines minimum (44x44pt) */
    minimum: 'min-h-[44px] min-w-[44px]',
    /** Material Design comfortable target (48x48dp) */
    comfortable: 'min-h-[48px] min-w-[48px]',
    /** Generous target for frequently-used actions */
    large: 'min-h-[56px] min-w-[56px]',
  },

  /** Z-index layering system for app UI elements */
  Z_INDEX: {
    /** Base content layer */
    base: 'z-0',
    /** Sticky headers and navigation */
    sticky: 'z-10',
    /** Floating action buttons */
    fab: 'z-20',
    /** Dropdown menus and tooltips */
    dropdown: 'z-30',
    /** Modal overlays */
    modal: 'z-40',
    /** Command palette */
    commandPalette: 'z-50',
    /** Toast notifications */
    toast: 'z-60',
  },

  /** Keyboard shortcuts display formatting */
  KEYBOARD: {
    /** Key badge styling (Cmd, K, etc.) */
    keyBadge: 'px-1.5 py-0.5 text-xs font-mono bg-muted border border-border rounded',
    /** Shortcut separator (between keys) */
    separator: 'text-muted-foreground/50',
  },
} as const;

/**
 * Modern Archive Card Variants
 *
 * Optimized card layouts for blog and work archive pages.
 * Fixes washed-out image issue with elevated images and lighter overlays.
 *
 * @example
 * ```tsx
 * // Elevated card with prominent image
 * <article className={ARCHIVE_CARD_VARIANTS.elevated.container}>
 *   <div className={ARCHIVE_CARD_VARIANTS.elevated.imageWrapper}>
 *     <Image className={ARCHIVE_CARD_VARIANTS.elevated.image} ... />
 *     <div className={ARCHIVE_CARD_VARIANTS.elevated.overlay} />
 *   </div>
 *   <div className={ARCHIVE_CARD_VARIANTS.elevated.content}>
 *     {content}
 *   </div>
 * </article>
 * ```
 */
export const ARCHIVE_CARD_VARIANTS = {
  /** Elevated image card - image on top, content below (recommended) */
  elevated: {
    container:
      'group rounded-xl border bg-card overflow-hidden hover:shadow-xl transition-all duration-300',
    imageWrapper:
      'relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden bg-muted/50 dark:bg-muted/30',
    image: 'object-cover group-hover:scale-105 transition-transform duration-500',
    /** Modern dark overlay matching blog cards */
    overlay: 'absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70',
    badgeContainer: 'absolute bottom-3 left-3 flex gap-2 z-10',
    badge: 'backdrop-blur-md bg-white/20 dark:bg-white/20 border border-white/30 text-white',
    glassCard: 'bg-background/95 backdrop-blur-sm rounded-lg p-4',
    content: 'p-4 md:p-6 space-y-3',
  },

  /** Background image card - reduced overlay for visibility */
  background: {
    container:
      'group relative rounded-xl border bg-card overflow-hidden hover:shadow-xl transition-all duration-300 min-h-[280px] md:min-h-[320px]',
    imageWrapper: 'absolute inset-0 z-0 bg-muted/50 dark:bg-muted/30',
    image: 'object-cover group-hover:scale-105 transition-transform duration-500',
    /** Modern dark overlay */
    overlay: 'absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70',
    content: 'relative z-10 p-6 md:p-8 flex flex-col justify-end h-full',
    /** Elevated glass card for content */
    glassCard: 'bg-background/80 backdrop-blur-md rounded-t-2xl p-6 border-t',
  },

  /** Side-by-side layout - image left, content right */
  sideBySide: {
    container:
      'group flex gap-4 rounded-xl border bg-card overflow-hidden hover:shadow-lg transition-shadow duration-300',
    imageWrapper: 'relative w-48 flex-shrink-0 overflow-hidden bg-muted/50 dark:bg-muted/30',
    image: 'object-cover group-hover:scale-105 transition-transform duration-500',
    overlay: 'hidden',
    badgeContainer: 'flex gap-2',
    badge: 'inline-flex',
    glassCard: 'hidden',
    content: 'flex-1 p-4 md:p-6 flex flex-col justify-between',
  },

  /** Hero card - large featured card */
  hero: {
    container:
      'group relative rounded-2xl overflow-hidden h-[500px] md:h-[600px] cursor-pointer hover:shadow-2xl transition-shadow duration-300',
    imageWrapper: 'absolute inset-0 z-0 bg-muted/50 dark:bg-muted/30',
    image: 'object-cover group-hover:scale-105 transition-transform duration-700',
    /** Modern dark overlay */
    overlay: 'absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70',
    content: 'absolute inset-x-0 bottom-0 p-6 md:p-12 text-white z-10',
    contentWrapper: 'max-w-3xl',
  },
} as const;

/**
 * Archive Animation Variants (Framer Motion)
 *
 * Pre-configured animation variants for consistent archive page animations.
 * Use with Framer Motion for staggered card reveals.
 *
 * @example
 * ```tsx
 * <motion.div
 *   variants={ARCHIVE_ANIMATIONS.container}
 *   initial="hidden"
 *   animate="visible"
 * >
 *   {items.map((item, i) => (
 *     <motion.div
 *       key={item.id}
 *       variants={ARCHIVE_ANIMATIONS.item}
 *       transition={{ delay: i * 0.05 }}
 *     >
 *       <Card />
 *     </motion.div>
 *   ))}
 * </motion.div>
 * ```
 */
export const ARCHIVE_ANIMATIONS = {
  /** Container with stagger children */
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  },

  /** Individual item fade-up */
  item: {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
  },

  /** Card hover animation */
  cardHover: {
    y: -8,
    scale: 1.02,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },

  /** Floating filter bar entrance */
  filterBar: {
    hidden: { y: -100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
  },
} as const;

/**
 * View Mode Options
 *
 * Layout configurations for different view modes in archive pages.
 *
 * @example
 * ```tsx
 * <div className={VIEW_MODES[viewMode].grid}>
 *   {items.map(item => <Card key={item.id} {...item} />)}
 * </div>
 * ```
 */
export const VIEW_MODES = {
  /** Compact grid - auto-fit columns */
  grid: {
    grid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
    cardPadding: 'p-4',
    imageHeight: 'aspect-[16/9]',
  },

  /** List view - single column with more detail */
  list: {
    grid: 'space-y-6',
    cardPadding: 'p-6',
    imageHeight: 'aspect-[21/9]',
  },

  /** Magazine - hero + grid */
  magazine: {
    grid: 'space-y-6',
    cardPadding: 'p-4 md:p-6',
    imageHeight: 'aspect-[16/9] md:aspect-[21/9]',
  },

  /** Masonry - Pinterest-style */
  masonry: {
    grid: 'columns-1 md:columns-2 lg:columns-3 gap-6',
    cardPadding: 'p-4 mb-6 break-inside-avoid',
    imageHeight: 'aspect-auto',
  },
} as const;
